import React, { useMemo } from "react";
import _ from "lodash";
import { useI18n } from "Hooks";
import { FilterOutlined } from "@toddle-edu/ds-icons";
import { FilterMenu, IconDisclosure } from "@toddle-edu/ds-web";
import { getUserCurriculumPrograms } from "modules/CommonSelectors";
import { PARENT, STAFF, STUDENT } from "Constants/stringConstants";
import CurriculumProgramLogo from "AppComponents/CurriculumProgramLogo";
import ViewModeFilter from "AppComponents/DualPanelManageMembersV2/components/Filter/ViewModeFilter";

export const FILTER_KEYS = {
  SCHOOL: "school",
  USER_TYPE: "userType",
};

const buildFilterOptions = ({ curriculumPrograms, t }) => [
  {
    key: FILTER_KEYS.SCHOOL,
    label: t("common:school"),
    selectionType: "checkbox",
    options: _.map(curriculumPrograms, cp => ({
      key: cp.id,
      label: cp.label || cp.acronym || cp.type,
      parent: FILTER_KEYS.SCHOOL,
      prefix: (
        <CurriculumProgramLogo curriculumProgramId={cp.id} size="small" />
      ),
    })),
  },
  {
    key: FILTER_KEYS.USER_TYPE,
    label: t("common:user_type"),
    selectionType: "checkbox",
    options: [
      { key: STAFF, label: t("common:staff"), parent: FILTER_KEYS.USER_TYPE },
      {
        key: STUDENT,
        label: t("common:student"),
        parent: FILTER_KEYS.USER_TYPE,
      },
      {
        key: PARENT,
        label: t("common:family_member"),
        parent: FILTER_KEYS.USER_TYPE,
      },
    ],
  },
];

const getSelectedCount = selectedFilters => {
  const schoolCount = _.size(_.get(selectedFilters, FILTER_KEYS.SCHOOL, []));
  const userTypeCount = _.size(
    _.get(selectedFilters, FILTER_KEYS.USER_TYPE, [])
  );
  return schoolCount + userTypeCount;
};

const MoreFilters = ({ selectedFilters, onFiltersChange }) => {
  const { t } = useI18n(["common"]);
  const curriculumPrograms = getUserCurriculumPrograms() || [];

  const filterOptions = useMemo(
    () => buildFilterOptions({ curriculumPrograms, t }),
    [curriculumPrograms]
  );

  const handleChange = newFilters => {
    onFiltersChange({
      [FILTER_KEYS.SCHOOL]: _.get(newFilters, FILTER_KEYS.SCHOOL, []),
      [FILTER_KEYS.USER_TYPE]: _.get(newFilters, FILTER_KEYS.USER_TYPE, []),
    });
  };

  const selectedCount = getSelectedCount(selectedFilters);

  const badgeProps = useMemo(
    () =>
      selectedCount > 0
        ? { value: selectedCount, variant: "dark", shape: "circle" }
        : undefined,
    [selectedCount]
  );

  return (
    <FilterMenu
      dsVersion="2.0"
      options={filterOptions}
      selectedItemKeys={selectedFilters}
      isSearchEnabled
      isSelectAllEnabled={false}
      isResetAllEnabled={false}
      showSelectedOptionsOnTop={false}
      variant="cascading"
      onChange={handleChange}
    >
      <IconDisclosure
        dsVersion="2.0"
        icon={<FilterOutlined />}
        type="outlined"
        variant="neutral"
        badgeProps={badgeProps}
      />
    </FilterMenu>
  );
};

const Filter = ({
  typeFilter,
  setTypeFilter,
  selectedFilters,
  onFiltersChange,
}) => {
  return (
    <>
      <ViewModeFilter typeFilter={typeFilter} setTypeFilter={setTypeFilter} />
      <MoreFilters
        selectedFilters={selectedFilters}
        onFiltersChange={onFiltersChange}
      />
    </>
  );
};

export default Filter;
